import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Phone, Mail } from "lucide-react";

export default function Register() {
    const { t } = useTranslation();
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [mode, setMode] = useState(params.get("mode") === "phone" ? "phone" : "email");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (mode === "email" && !email) { setError("Email is required"); return; }
        if (mode === "phone" && !phone) { setError("Mobile number is required"); return; }
        setLoading(true);
        const payload = { name, password };
        if (email) payload.email = email;
        if (phone) payload.phone = phone;
        const res = await register(payload);
        setLoading(false);
        if (res.ok) navigate("/dashboard");
        else setError(res.error);
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-md p-card p-8 sm:p-10" data-testid="register-card">
                <div className="flex items-center gap-2 mb-6">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ background: "#7F1D1D", color: "#D4AF37" }}>
                        <Sparkles size={18} />
                    </div>
                    <div className="font-display text-3xl" style={{ color: "#1E1B4B" }}>{t("register.h")}</div>
                </div>
                <p className="text-sm mb-4" style={{ color: "#525252" }}>{t("register.sub")}</p>

                <div className="flex rounded-full p-1 mb-5" style={{ background: "#F5F0E6" }} data-testid="register-mode-toggle">
                    <button type="button" onClick={() => setMode("email")}
                            className={`flex-1 text-xs px-3 py-2 rounded-full inline-flex items-center justify-center gap-1.5`}
                            style={{ background: mode === "email" ? "#1E1B4B" : "transparent", color: mode === "email" ? "#FDFBF7" : "#1E1B4B" }}
                            data-testid="register-mode-email">
                        <Mail size={12} /> Email
                    </button>
                    <button type="button" onClick={() => setMode("phone")}
                            className={`flex-1 text-xs px-3 py-2 rounded-full inline-flex items-center justify-center gap-1.5`}
                            style={{ background: mode === "phone" ? "#1E1B4B" : "transparent", color: mode === "phone" ? "#FDFBF7" : "#1E1B4B" }}
                            data-testid="register-mode-phone">
                        <Phone size={12} /> Mobile
                    </button>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("register.name")}</label>
                        <input required value={name} onChange={(e) => setName(e.target.value)}
                               className="w-full px-4 py-3 rounded-full border"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="register-name-input" />
                    </div>
                    {mode === "email" ? (
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("register.email")}</label>
                            <input type="email" required={mode === "email"} value={email} onChange={(e) => setEmail(e.target.value)}
                                   className="w-full px-4 py-3 rounded-full border"
                                   style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="register-email-input" />
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("register.phone")}</label>
                            <input type="tel" required={mode === "phone"} value={phone} onChange={(e) => setPhone(e.target.value)}
                                   placeholder={t("register.phone_hint")}
                                   className="w-full px-4 py-3 rounded-full border"
                                   style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                   data-testid="register-phone-input" />
                            <div className="text-xs mt-1" style={{ color: "#525252" }}>{t("register.phone_hint")}</div>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("register.password")}</label>
                        <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                               className="w-full px-4 py-3 rounded-full border"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="register-password-input" />
                    </div>
                    {error && <div className="text-sm" style={{ color: "#7F1D1D" }} data-testid="register-error">{error}</div>}
                    <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="register-submit-button">
                        {loading ? t("register.submitting") : t("register.submit")}
                    </button>
                </form>
                <div className="mt-6 text-sm text-center" style={{ color: "#525252" }}>
                    {t("register.have")} <Link to="/login" className="underline" style={{ color: "#1E1B4B" }} data-testid="register-login-link">{t("register.have_link")}</Link>
                </div>
            </div>
        </div>
    );
}
